import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Stack,
  Avatar,
  Typography,
  Skeleton,
  Drawer,
  IconButton,
  useMediaQuery,
  Divider,
  Grid,
  Card,
  CardContent,
  Button,
  Paper
} from '@mui/material';
import { motion } from 'framer-motion';
// import { UserOutlined } from '@ant-design/icons';
import CloseIcon from '@mui/icons-material/Close';
import SideBySideMain from './SideBySideMain';
import FieldBasedComparison from './FieldBasedComparison';
import CompanyAnalysis from 'sections/dashboard/CompanyAnalysis';
import { useDispatch, useSelector } from 'react-redux';
import BusinessIcon from '@mui/icons-material/Business';
import { setFavicon, openModal } from 'store/reducers/actions';
import FreeBlockModal from 'sections/premiumBlock/FreeBlockModal';
import useSalesContact from 'hooks/useSalesContact';
import useAuth from 'hooks/useAuth';
// import LockIcon from '@mui/icons-material/Lock';
// import EmailIcon from '@mui/icons-material/Email';
// import { useTheme } from '@mui/material/styles';

// Get nested property from object safely
// const getNestedProperty = (obj, path) => {
//   if (!obj) return null;

//   const properties = path.split('.');
//   let current = obj;

//   for (const property of properties) {
//     if (current === null || current === undefined) {
//       return null;
//     }
//     current = current[property];
//   }

//   return current;
// };

// Create a Favicon component similar to CompetitorsStep.js
const Favicon = ({ company, size = 40 }) => {
  const analysisResults = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults?.analysis_results);

  const favicon = useMemo(() => {
    if (!company || !analysisResults) return null;

    return analysisResults.find((item) => {
      const companyDomain = company.toLowerCase().replace(/^https?:\/\//, '');
      const itemDomain = item.url.toLowerCase().replace(/^https?:\/\//, '');
      return itemDomain.includes(companyDomain) || companyDomain.includes(itemDomain);
    })?.favicon;
  }, [company, analysisResults]);

  return favicon ? (
    <Avatar
      src={favicon}
      alt="Company logo"
      sx={{
        width: size,
        height: size,
        flexShrink: 0
      }}
    />
  ) : (
    <Avatar
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        bgcolor: 'grey.100',
        color: 'text.secondary'
      }}
    >
      <BusinessIcon style={{ fontSize: size * 0.5 }} />
    </Avatar>
  );
};

const ensureHttpProtocol = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

const transformCompanyData = (analysisData) => {
  if (!analysisData || !Array.isArray(analysisData)) return [];

  return analysisData.map((company) => {
    // Find company name from overview_result
    const serviceName = company.overview_result?.find((item) => 'Service Name' in item)?.['Service Name'];

    return {
      company_name: serviceName || company.url || 'Unnamed Company',
      name: serviceName || company.url || 'Unnamed Company',
      website: ensureHttpProtocol(company.url),
      // You can add logo_url if it's available in your data
      favicon: company.favicon,
      // Structure the data as expected by CompanyAnalysis
      analysis_data: {
        overview_result: {
          result: [
            company.overview_result?.reduce((acc, curr) => {
              const [key, value] = Object.entries(curr)[0];
              acc[key] = value;
              return acc;
            }, {})
          ]
        },
        business_analysis: company.business_analysis,
        market_trends: company.market_trends,
        usp: company.usp
      }
    };
  });
};

const CompanySelector = ({ companies, selectedCompany, onCompanyClick }) => {
  const dispatch = useDispatch();

  // Store favicons in Redux when companies data is received
  useEffect(() => {
    if (companies && companies.length > 0) {
      companies.forEach((company) => {
        if (company.favicon) {
          dispatch(setFavicon(company.website || company.url || company.name || company.company_name, company.favicon));
        }
      });
    }
  }, [companies, dispatch]);

  // Check if we have valid data to display
  const isDataReady = companies && companies.length > 0;

  // If no companies or data isn't ready, show skeleton loading state
  if (!isDataReady) {
    return (
      <Box sx={{ width: '100%' }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ mb: 0.25 }}>
              Companies
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View analysis for different companies
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {Array(6)
              .fill(null)
              .map((_, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Skeleton variant="text" width={100} />
                  </Stack>
                </Grid>
              ))}
          </Grid>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ mb: 0.25 }}>
            Companies
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View analysis for different companies
          </Typography>
        </Box>

        <Box sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Grid container spacing={2}>
            {companies.map((company, index) => {
              const isSelected =
                selectedCompany &&
                ((company.id && selectedCompany.id === company.id) ||
                  (company.company_id && selectedCompany.company_id === company.company_id) ||
                  (company.task_id && selectedCompany.task_id === company.task_id) ||
                  (company.company_name && selectedCompany.company_name === company.company_name));

              // const domain = company.website || company.url || company.name || company.company_name;

              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={`company-${index}`}>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Box
                      onClick={() => onCompanyClick(company)}
                      sx={{
                        cursor: 'pointer',
                        p: 1,
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          background: 'rgba(0, 0, 0, 0.02)'
                        },
                        ...(isSelected && {
                          background: 'rgba(94, 53, 177, 0.08)'
                        }),
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <Box>
                          <Favicon company={company.name || company.company_name} size={40} />
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? 'primary.main' : 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.2,
                            width: '100%'
                          }}
                        >
                          {company.name || company.company_name || 'Unnamed Company'}
                        </Typography>
                      </Stack>
                    </Box>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
};

const CompanySlidePanel = ({ open, onClose, selectedCompany, companies, onCompanyClick }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [isMinimized, setIsMinimized] = useState(false);
  const panelRef = useRef(null);

  // Track scroll position within the panel to show/hide minimized selector
  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      const scrollContainer = document.querySelector('.company-panel-scroll-container');
      if (scrollContainer) {
        setIsMinimized(scrollContainer.scrollTop > 200);
      }
    };

    const scrollContainer = document.querySelector('.company-panel-scroll-container');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [open]);

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: -25,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(250, 250, 250, 0.8)',
          backdropFilter: 'blur(1px)',
          zIndex: 2500,
          display: open ? 'block' : 'none',
          transition: 'opacity 0.3s ease-in-out',
          opacity: open ? 1 : 0
        }}
        onClick={onClose}
      />

      {/* Minimized company selector that appears when scrolling within panel */}
      {open && isMinimized && (
        <Box
          sx={{
            position: 'fixed',
            top: -23,
            right: 0,
            margin: 0,
            padding: 0,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'row',
            bgcolor: 'background.paper',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '0 0 0 8px',
            overflow: 'hidden'
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1, m: 0 }}>
            {companies &&
              companies.map((company, index) => {
                // Determine if this company is selected for highlight
                const isSelected =
                  selectedCompany &&
                  ((company.id && selectedCompany.id === company.id) ||
                    (company.company_id && selectedCompany.company_id === company.company_id) ||
                    (company.task_id && selectedCompany.task_id === company.task_id) ||
                    (company.company_name && selectedCompany.company_name === company.company_name));

                return (
                  <Box key={`mini-company-${index}`} sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={() => onCompanyClick(company)}
                      sx={{
                        p: 0.5,
                        border: isSelected ? '2px solid' : '0px solid',
                        borderColor: isSelected ? 'primary.main' : 'transparent',
                        borderRadius: '50%'
                      }}
                    >
                      <Favicon company={company.name || company.company_name} size={32} />
                    </IconButton>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{
                        fontSize: '0.65rem',
                        mt: 0.5,
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? 'primary.main' : 'text.primary'
                      }}
                    >
                      {(company.name || company.company_name || 'Company').split(' ')[0]}
                    </Typography>
                  </Box>
                );
              })}
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: isMobile ? '0%' : '60%',
          zIndex: 2502,
          display: open ? 'block' : 'none'
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: isMobile ? 90 : 0,
            right: isMobile ? 10 : -25,
            bgcolor: 'background.paper',
            width: 50,
            height: 50,
            border: '2px solid',
            borderColor: 'divider',
            boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.1)',
            borderRadius: '50%',
            '&:hover': {
              bgcolor: '#f0f0f0'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: 30 }} />
        </IconButton>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        variant="persistent"
        sx={{
          zIndex: 2501,
          '& .MuiDrawer-paper': {
            width: isMobile ? '98%' : '60%',
            position: 'fixed',
            transition: 'transform 0.3s ease-in-out',
            height: '100%',
            top: 0,
            borderLeft: '2px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <Box sx={{ position: 'relative', height: '100%' }} ref={panelRef}>
          <Box
            className="company-panel-scroll-container"
            sx={{
              px: 4,
              pb: 4,
              height: '100%',
              overflow: 'auto'
            }}
          >
            {/* Company Picker at the top of the panel - Multi-row grid */}
            <Box sx={{ mb: 3, mt: 3, width: '100%' }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Company Reports
              </Typography>

              <Box sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={2}>
                  {companies &&
                    companies.map((company, index) => {
                      // Determine if this company is selected for highlight
                      const isSelected =
                        selectedCompany &&
                        ((company.id && selectedCompany.id === company.id) ||
                          (company.company_id && selectedCompany.company_id === company.company_id) ||
                          (company.task_id && selectedCompany.task_id === company.task_id) ||
                          (company.company_name && selectedCompany.company_name === company.company_name));

                      return (
                        <Grid item xs={6} sm={4} md={3} key={`panel-company-${index}`}>
                          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Box
                              sx={{
                                cursor: 'pointer',
                                p: 1,
                                borderRadius: 2,
                                transition: 'all 0.2s',
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': {
                                  background: 'rgba(0, 0, 0, 0.02)'
                                },
                                ...(isSelected && {
                                  background: 'rgba(94, 53, 177, 0.08)'
                                }),
                                height: '100%',
                                width: '100%'
                              }}
                              onClick={() => onCompanyClick(company)}
                            >
                              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                <Box>
                                  <Favicon company={company.name || company.company_name} size={40} />
                                </Box>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isSelected ? 'primary.main' : 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    lineHeight: 1.2,
                                    width: '100%'
                                  }}
                                >
                                  {company.name || company.company_name || 'Unnamed Company'}
                                </Typography>
                              </Stack>
                            </Box>
                          </motion.div>
                        </Grid>
                      );
                    })}
                </Grid>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Company analysis content */}
            {selectedCompany && <CompanyAnalysis companyData={selectedCompany} />}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

const SideBySideReport = ({ analysisData, companyData }) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [slidePanelOpen, setSlidePanelOpen] = useState(false);
  const contentRef = useRef(null);
  const { user } = useAuth();
  const { handleSalesContact, salesContactDialog } = useSalesContact({ user, source: 'Enterprise — side-by-side analysis' });

  // Check access control
  const hasSideBySideAccess = companyData?.access?.features?.side_by_side_analysis?.enabled ?? true;
  const hasFieldByFieldAccess = companyData?.access?.features?.field_by_field_analysis?.enabled ?? true;

  // Plan-based behavior
  const dispatch = useDispatch();
  const plan = useSelector((state) => state.proAccountReducer?.plan);
  const isEnterprisePro = plan === 'Enterprise Pro' || plan === 'Enterprise Pro Max';
  const isFreePlan = plan === 'Free' || plan === 'Trial' || !plan;
  const trialWasActivated = useSelector((state) => state.proAccountReducer?.trial_was_activated);
  const userCountry = useSelector((state) => state.proAccountReducer?.country);
  const restrictedCountries = ['IN', 'VN', 'CN', 'BD', 'PK', 'ID'];
  const trialAllowed = !trialWasActivated && !restrictedCountries.includes(userCountry);

  const handleTrialClick = () => {
    dispatch(
      openModal({
        title: 'Unlock Competitors Analysis',
        subtitle: 'Upgrade to Pro to view all insights across competitors.',
        trial: trialAllowed
      })
    );
  };

  const Gate = ({ children, cta }) => (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.85 }}>{children}</Box>
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 3,
          backgroundColor: 'rgba(255,255,255,0.5)'
        }}
      >
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 6,
              p: { xs: 2, md: 3 },
              width: '92%',
              maxWidth: 720,
              textAlign: 'left'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {cta?.title || 'Unlock full report'}
            </Typography>
            {Array.isArray(cta?.bullets) && cta.bullets.length > 0 && (
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {cta.bullets.map((b, i) => (
                  <Grid key={i} item xs={12} sm={6}>
                    <Typography variant="body2">• {b}</Typography>
                  </Grid>
                ))}
              </Grid>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleTrialClick}
                component={motion.button}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                sx={{ px: 3, py: 1.25, background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)', boxShadow: 'none' }}
              >
                {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Cancel anytime
              </Typography>
            </Box>
          </Box>
        </motion.div>
      </Paper>
    </Box>
  );

  const handleCompanyClick = useCallback((company) => {
    setSelectedCompany(company);
    setSlidePanelOpen(true);
  }, []);

  // Memoize the companies data to prevent unnecessary recalculations
  const companiesData = useMemo(() => transformCompanyData(analysisData || []), [analysisData]);

  // Memoize the company selector to prevent unnecessary re-renders
  const CompanySelectorMemo = useMemo(
    () => <CompanySelector companies={companiesData} selectedCompany={selectedCompany} onCompanyClick={handleCompanyClick} />,
    [companiesData, selectedCompany, handleCompanyClick]
  );

  // Memoize the slide panel to prevent unnecessary re-renders
  const CompanySlidePanelMemo = useMemo(
    () => (
      <CompanySlidePanel
        open={slidePanelOpen}
        onClose={() => setSlidePanelOpen(false)}
        selectedCompany={selectedCompany}
        companies={companiesData}
        onCompanyClick={handleCompanyClick}
      />
    ),
    [slidePanelOpen, selectedCompany, companiesData, handleCompanyClick]
  );

  // External citation highlight and scroll for competitors report
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const clearExternalHighlights = () => {
      const nodes = container.querySelectorAll('.external-citation-highlight');
      nodes.forEach((node) => {
        const parent = node.parentNode;
        if (!parent) return;
        parent.replaceChild(document.createTextNode(node.textContent), node);
        parent.normalize();
      });
    };

    const onHighlight = (e) => {
      try {
        const target = String(e.detail?.text || '').trim();
        if (!target) return;
        if (!contentRef.current) return;

        clearExternalHighlights();

        const walker = document.createTreeWalker(contentRef.current, NodeFilter.SHOW_TEXT, null);
        let foundNode = null;
        let foundStart = -1;
        while (walker.nextNode()) {
          const node = walker.currentNode;
          const val = node.nodeValue || '';
          const idx = val.toLowerCase().indexOf(target.toLowerCase());
          if (idx >= 0) {
            foundNode = node;
            foundStart = idx;
            break;
          }
        }

        if (foundNode && foundStart >= 0) {
          const range = document.createRange();
          range.setStart(foundNode, foundStart);
          range.setEnd(foundNode, foundStart + target.length);
          const span = document.createElement('span');
          span.className = 'external-citation-highlight';
          span.style.backgroundColor = 'rgba(255, 215, 0, 0.35)';
          span.style.borderRadius = '2px';
          span.style.padding = '1px 2px';
          try {
            range.surroundContents(span);
          } catch (err) {
            span.appendChild(range.extractContents());
            range.insertNode(span);
          }
          setTimeout(() => span.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
        }
      } catch (err) {
        // ignore
      }
    };

    const onClear = () => clearExternalHighlights();

    window.addEventListener('comp-analysis:highlight', onHighlight);
    window.addEventListener('comp-analysis:clear-highlight', onClear);
    return () => {
      window.removeEventListener('comp-analysis:highlight', onHighlight);
      window.removeEventListener('comp-analysis:clear-highlight', onClear);
      clearExternalHighlights();
    };
  }, []);

  // Suppress click-driven re-renders when there is an active text selection
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const hasSelection = () => {
      try {
        const sel = window.getSelection();
        return !!(sel && !sel.isCollapsed && String(sel.toString() || '').trim().length > 0);
      } catch (_) {
        return false;
      }
    };
    const onClickCapture = (e) => {
      if (hasSelection()) {
        try {
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        } catch (_) {
          /* ignore */
        }
        try {
          e.stopPropagation();
        } catch (_) {
          /* ignore */
        }
        try {
          e.preventDefault();
        } catch (_) {
          /* ignore */
        }
      }
    };
    el.addEventListener('click', onClickCapture, true);
    return () => {
      try {
        el.removeEventListener('click', onClickCapture, true);
      } catch (_) {
        /* ignore */
      }
    };
  }, []);

  // Render locked state for side-by-side analysis
  const renderLockedSideBySide = () => (
    <Card sx={{ width: '100%', mb: 3, borderRadius: 2, position: 'relative' }}>
      <IconButton
        onClick={() => setSlidePanelOpen(false)}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1
        }}
      >
        <CloseIcon />
      </IconButton>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h5" color="text.secondary">
            Side-by-Side Analysis
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              We&apos;ve added a new Side-by-Side Analysis tool for Enterprise Pro Max plans. Take a look at what&apos;s possible and let us
              know if you&apos;d like to try it.
            </Typography>
            <Typography
              variant="body2"
              component="a"
              href="/sbs-example"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              View example report →
            </Typography>
          </Box>
          <Button variant="gradient" color="primary" onClick={() => handleSalesContact()}>
            Talk to Askpot Team
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        mb: 4,
        pt: 2
      }}
      ref={contentRef}
    >
      <Stack spacing={3}>
        {useSelector((state) => state.modalReducer.isOpen) ? <FreeBlockModal /> : null}
        {/* Main Company Selector at the top */}
        {CompanySelectorMemo}

        {/* SBS Analysis - enterprise-only locked card; free shows CTA overlay */}
        <Box id="section-sbs-analysis">
          {hasSideBySideAccess ? (
            <SideBySideMain />
          ) : isEnterprisePro ? (
            renderLockedSideBySide()
          ) : (
            <>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                Side-by-Side Analysis
              </Typography>
              <Gate
                cta={{
                  title: 'Unlock Side-by-Side Analysis',
                  bullets: ['Overview & key metrics', 'Competitive index', 'Feature matrix', 'Heatmap scorecard', 'Positioning matrices']
                }}
              >
                <SideBySideMain />
              </Gate>
            </>
          )}
        </Box>

        {/* Field-by-Field Analysis - always show if we have access */}
        <Box>
          {!hasFieldByFieldAccess && (
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
              Field-Based Comparison
            </Typography>
          )}
          <FieldBasedComparison
            analysisData={analysisData}
            companyData={companyData}
            showSecondaryMenu={false}
            isPaywalled={isFreePlan}
            cta={{
              title: 'Unlock Field-Based Comparison',
              bullets: ['Overview fields', 'USP & Distinctions', 'Market trends', 'Pricing analysis']
            }}
          />
        </Box>

        {/* Company Slide Panel */}
        {CompanySlidePanelMemo}
        {salesContactDialog}
      </Stack>
    </Box>
  );
};

export default SideBySideReport;
